import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import {
  getWorkHourSettings,
  getWorkHourSetting,
  createWorkHourSetting,
  updateWorkHourSetting,
} from '../../actions/work-hour-settings.action'
import { Reorder } from '../../util/reorder'
import './scss/work-hour-settings.scss'
import moment from 'moment'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
}
const DAY_SHORT = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
}

// Types used inside time period bands (weekday rules and exception time periods)
const TIME_TYPES = [
  { value: 'STANDARD', key: 'Standard' },
  { value: 'OVERTIME_1', key: 'Overtime' },
  { value: 'PUBLIC_HOLIDAY', key: 'Holiday' },
]

// Categories used to classify an exception day itself
const EXCEPTION_TYPES = [
  { value: 'PUBLIC_HOLIDAY', key: 'Holiday' },
  { value: 'RDO', key: 'RDO' },
  { value: 'CUSTOM', key: 'Custom' },
]

// Whether Time Periods are defined by clock time (Start/End) or by number of Hours
const TIME_PERIOD_TYPES = [
  { value: 'TIME_PERIOD', key: 'Time Period' },
  { value: 'HOURLY', key: 'Hourly' },
]


function buildEmptyWeekdayRules() {
  const r = {}
  DAYS.forEach((d) => { r[d] = [] })
  return r
}

export function validateTimeBands(bands) {
  if (!bands || bands.length < 2) return null
  const sorted = [...bands].sort((a, b) => a.start.localeCompare(b.start))
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].end > sorted[i + 1].start) {
      return `Overlap: ${sorted[i].start}–${sorted[i].end} overlaps with ${sorted[i + 1].start}–${sorted[i + 1].end}`
    }
  }
  return null
}

function formatTypeLabel(time_type) {
  return TIME_TYPES.find((t) => t.value === time_type)?.key || time_type
}

const BLANK_FORM = {
  name: '', isActive: true,
  timePeriodType: 'TIME_PERIOD', showTypeChangeWarning: false,
  selectedDay: 'monday',
  // Weekday time period editing state
  pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0,
  pError: '', selectedPeriodIdx: null, showAddPeriod: false,
  // Exception editing state
  eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
  // Exception time period sub-editing state
  epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
  epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
  eError: '', selectedExcIdx: null, showAddException: false,
  nameError: '',
}

/** True if a setting already has any configured Time Periods or Date Exceptions. */
function hasExistingPeriodData(weekdayRules, exceptions) {
  const hasWeekdayPeriods = Object.values(weekdayRules || {}).some((arr) => (arr || []).length > 0)
  const hasExceptionPeriods = (exceptions || []).some((e) => (e.time_periods || []).length > 0)
  return hasWeekdayPeriods || hasExceptionPeriods
}

class WorkHourSettingsPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      settings: [],
      selectedId: null,
      weekdayRules: buildEmptyWeekdayRules(),
      exceptions: [],
      saving: false,
      ...BLANK_FORM,
    }
  }

  componentDidMount() {
    this.loadSettings()
  }

  loadSettings = (keepSelection = false) => {
    this.props.getWorkHourSettings().then((result) => {
      const list = Array.isArray(result) ? result : (result?.data || [])
      this.setState({ settings: list }, () => {
        if (keepSelection && this.state.selectedId) {
          this.selectSetting(this.state.selectedId)
        } else if (!keepSelection && !this.state.selectedId && list.length > 0) {
          this.selectSetting(list[0].id)
        }
      })
    }).catch(() => {})
  }

  selectSetting = (id) => {
    this.props.getWorkHourSetting(id).then((result) => {
      const s = result?.data || result
      if (!s) return
      this.populateForm(s)
    }).catch(() => {})
  }

  populateForm = (s) => {
    // Assigns a stable Order (defaulting to array position) to bands loaded without one
    const withOrder = (bands) => bands.map((b, i) => ({ ...b, order: b.order ?? i }))

    const weekdayRules = buildEmptyWeekdayRules()
    if (s.weekday_rules) {
      DAYS.forEach((d) => {
        if (Array.isArray(s.weekday_rules[d])) {
          weekdayRules[d] = withOrder(s.weekday_rules[d])
        }
      })
    }
    // Support both new format ({ date, name, exception_type, time_periods })
    // and old format ({ name, dates, rules }) for backward compatibility
    const exceptions = []
    if (Array.isArray(s.date_exceptions)) {
      s.date_exceptions.forEach((item) => {
        if (item.date) {
          // New format — individual exception with its own time_periods
          exceptions.push({
            date: item.date,
            name: item.name,
            exception_type: item.exception_type || 'PUBLIC_HOLIDAY',
            time_periods: withOrder(Array.isArray(item.time_periods) ? item.time_periods : []),
          })
        } else if (item.dates) {
          // Old grouped format — flatten and migrate
          const exception_type = item.rules?.[0]?.time_type || 'PUBLIC_HOLIDAY'
          ;(item.dates || []).forEach((date) => {
            exceptions.push({
              date,
              name: item.name,
              exception_type,
              time_periods: withOrder(Array.isArray(item.rules) ? item.rules : []),
            })
          })
        }
      })
    }
    this.setState({
      selectedId: s.id,
      weekdayRules,
      exceptions,
      name: s.name || '',
      isActive: s.is_active !== false,
      timePeriodType: s.time_period_type || 'TIME_PERIOD',
      showTypeChangeWarning: false,
      selectedDay: 'monday',
      pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
      nameError: '',
    })
  }

  startNew = () => {
    this.setState({
      selectedId: null,
      weekdayRules: buildEmptyWeekdayRules(),
      exceptions: [],
      ...BLANK_FORM,
    })
  }

  // ── Time Period Type ──────────────────────────────────────────────────────────

  changeTimePeriodType = (newType) => {
    const { weekdayRules, exceptions } = this.state
    this.setState({
      timePeriodType: newType,
      showTypeChangeWarning: hasExistingPeriodData(weekdayRules, exceptions),
    })
  }

  // ── Weekday time period helpers ──────────────────────────────────────────────

  selectPeriod = (idx) => {
    const { weekdayRules, selectedDay } = this.state
    const p = weekdayRules[selectedDay][idx]
    this.setState({
      selectedPeriodIdx: idx, showAddPeriod: false,
      pStart: p.start || '', pEnd: p.end || '', pHours: p.hours ?? '', pType: p.time_type,
      pMultiplier: p.multiplier, pError: '',
    })
  }

  cancelPeriodEdit = () => {
    this.setState({
      selectedPeriodIdx: null, showAddPeriod: false,
      pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
    })
  }

  addOrUpdatePeriod = () => {
    const { weekdayRules, selectedDay, pStart, pEnd, pHours, pType, pMultiplier, selectedPeriodIdx, timePeriodType } = this.state
    const list = [...(weekdayRules[selectedDay] || [])]
    const order = selectedPeriodIdx !== null ? list[selectedPeriodIdx].order : list.length
    let band

    if (timePeriodType === 'HOURLY') {
      if (!pHours || parseFloat(pHours) <= 0) { this.setState({ pError: 'Hours must be greater than 0' }); return }
      band = { hours: parseFloat(pHours), time_type: pType, multiplier: parseFloat(pMultiplier) || 1.0, order }
    } else {
      if (!pStart || !pEnd) { this.setState({ pError: 'Start and end time are required' }); return }
      if (pEnd <= pStart) { this.setState({ pError: 'End time must be after start time' }); return }
      band = { start: pStart, end: pEnd, time_type: pType, multiplier: parseFloat(pMultiplier) || 1.0, order }
    }

    if (selectedPeriodIdx !== null) { list[selectedPeriodIdx] = band } else { list.push(band) }

    if (timePeriodType !== 'HOURLY') {
      const overlapErr = validateTimeBands(list)
      if (overlapErr) { this.setState({ pError: overlapErr }); return }
    }

    this.setState({
      weekdayRules: { ...weekdayRules, [selectedDay]: list },
      pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
    })
  }

  handlePeriodDragEnd = (result) => {
    if (!result.destination) return
    const { weekdayRules, selectedDay } = this.state
    const reordered = Reorder(weekdayRules[selectedDay], result.source.index, result.destination.index)
      .map((b, i) => ({ ...b, order: i }))
    this.setState({ weekdayRules: { ...weekdayRules, [selectedDay]: reordered } })
  }

  deletePeriod = (idx) => {
    const { weekdayRules, selectedDay } = this.state
    this.setState({
      weekdayRules: {
        ...weekdayRules,
        [selectedDay]: weekdayRules[selectedDay].filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i })),
      },
      pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
    })
  }

  // ── Exception helpers ────────────────────────────────────────────────────────

  selectException = (idx) => {
    const e = this.state.exceptions[idx]
    this.setState({
      selectedExcIdx: idx, showAddException: false,
      eDate: e.date, eName: e.name, eExceptionType: e.exception_type || 'PUBLIC_HOLIDAY',
      eTimePeriods: [...(e.time_periods || [])],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  startAddException = () => {
    this.setState({
      showAddException: true, selectedExcIdx: null,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  cancelExceptionEdit = () => {
    this.setState({
      selectedExcIdx: null, showAddException: false,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  addOrUpdateException = () => {
    const { exceptions, eDate, eName, eExceptionType, eTimePeriods, selectedExcIdx } = this.state
    if (!eDate || !eName) { this.setState({ eError: 'Date and name are required' }); return }
    const formatted = moment(eDate).isValid() ? moment(eDate).format('YYYY-MM-DD') : eDate
    const list = [...exceptions]
    const entry = { date: formatted, name: eName, exception_type: eExceptionType, time_periods: [...eTimePeriods] }
    if (selectedExcIdx !== null) {
      list[selectedExcIdx] = entry
    } else {
      if (list.some((e) => e.date === formatted)) { this.setState({ eError: 'This date is already added' }); return }
      list.push(entry)
    }
    this.setState({
      exceptions: list,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
    })
  }

  deleteException = (idx) => {
    this.setState({
      exceptions: this.state.exceptions.filter((_, i) => i !== idx),
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
    })
  }

  // ── Exception time period sub-helpers ────────────────────────────────────────

  selectExcPeriod = (idx) => {
    const p = this.state.eTimePeriods[idx]
    this.setState({
      selectedExcPeriodIdx: idx, showAddExcPeriod: false,
      epStart: p.start || '', epEnd: p.end || '', epHours: p.hours ?? '', epType: p.time_type, epMultiplier: p.multiplier, epError: '',
    })
  }

  cancelExcPeriodEdit = () => {
    this.setState({
      selectedExcPeriodIdx: null, showAddExcPeriod: false,
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0, epError: '',
    })
  }

  addOrUpdateExcPeriod = () => {
    const { eTimePeriods, epStart, epEnd, epHours, epType, epMultiplier, selectedExcPeriodIdx, timePeriodType } = this.state
    const list = [...eTimePeriods]
    const order = selectedExcPeriodIdx !== null ? list[selectedExcPeriodIdx].order : list.length
    let band

    if (timePeriodType === 'HOURLY') {
      if (!epHours || parseFloat(epHours) <= 0) { this.setState({ epError: 'Hours must be greater than 0' }); return }
      band = { hours: parseFloat(epHours), time_type: epType, multiplier: parseFloat(epMultiplier) || 1.0, order }
    } else {
      if (!epStart || !epEnd) { this.setState({ epError: 'Start and end time are required' }); return }
      if (epEnd <= epStart) { this.setState({ epError: 'End time must be after start time' }); return }
      band = { start: epStart, end: epEnd, time_type: epType, multiplier: parseFloat(epMultiplier) || 1.0, order }
    }

    if (selectedExcPeriodIdx !== null) { list[selectedExcPeriodIdx] = band } else { list.push(band) }

    if (timePeriodType !== 'HOURLY') {
      const overlapErr = validateTimeBands(list)
      if (overlapErr) { this.setState({ epError: overlapErr }); return }
    }

    this.setState({
      eTimePeriods: list,
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
    })
  }

  handleExcPeriodDragEnd = (result) => {
    if (!result.destination) return
    const reordered = Reorder(this.state.eTimePeriods, result.source.index, result.destination.index)
      .map((b, i) => ({ ...b, order: i }))
    this.setState({ eTimePeriods: reordered })
  }

  deleteExcPeriod = (idx) => {
    this.setState({
      eTimePeriods: this.state.eTimePeriods.filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i })),
      epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
    })
  }

  // ── Save ─────────────────────────────────────────────────────────────────────

  buildExceptionPayload = () => {
    return this.state.exceptions.map(({ date, name, exception_type, time_periods }) => ({
      date,
      name,
      exception_type: exception_type || 'PUBLIC_HOLIDAY',
      time_periods: time_periods || [],
    }))
  }

  save = () => {
    const { name, isActive, weekdayRules, timePeriodType, selectedId } = this.state
    if (!name.trim()) { this.setState({ nameError: 'Name is required' }); return }
    const payload = {
      name: name.trim(),
      is_active: isActive,
      time_period_type: timePeriodType,
      weekday_rules: weekdayRules,
      date_exceptions: this.buildExceptionPayload(),
    }
    this.setState({ saving: true })
    const action = selectedId
      ? this.props.updateWorkHourSetting(selectedId, payload)
      : this.props.createWorkHourSetting(payload)
    action.then(() => {
      this.setState({ saving: false })
      this.loadSettings(!!selectedId)
    }).catch(() => { this.setState({ saving: false }) })
  }

  cancel = () => {
    if (this.state.selectedId) { this.selectSetting(this.state.selectedId) } else { this.startNew() }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  render() {
    const {
      settings, selectedId, saving, nameError,
      name, isActive, timePeriodType, showTypeChangeWarning,
      selectedDay, weekdayRules, exceptions,
      pStart, pEnd, pHours, pType, pMultiplier, pError, selectedPeriodIdx, showAddPeriod,
      eDate, eName, eExceptionType, eTimePeriods, eError, selectedExcIdx, showAddException,
      epStart, epEnd, epHours, epType, epMultiplier, epError, selectedExcPeriodIdx, showAddExcPeriod,
    } = this.state
    const { writePermission } = this.props
    const isHourly = timePeriodType === 'HOURLY'
    const currentPeriods = weekdayRules[selectedDay] || []
    // Type + Multiplier are always present; Hourly swaps Start/End (2 cols) for Hours (1 col);
    // the drag handle and action columns are writePermission-only.
    const periodColSpan = (isHourly ? 3 : 4) + (writePermission ? 2 : 0)
    const excPeriodColSpan = periodColSpan
    const excColSpan = writePermission ? 5 : 4
    const excEditOpen = showAddException || selectedExcIdx !== null
    // Rows can only be dragged when nothing is mid-add/edit, to avoid reordering under an uncommitted row
    const periodDragDisabled = !writePermission || showAddPeriod || selectedPeriodIdx !== null
    const excPeriodDragDisabled = !writePermission || showAddExcPeriod || selectedExcPeriodIdx !== null

    const periodInputRow = (isNew) => (
      <tr className="whs-edit-row">
        {writePermission && <td className="whs-drag-cell" />}
        {isHourly ? (
          <td>
            <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={pHours}
              onChange={(e) => this.setState({ pHours: e.target.value, pError: '' })}
              disabled={!writePermission} data-cy="period-hours" />
          </td>
        ) : (
          <>
            <td>
              <input type="time" className="form-control whs-inline-input" value={pStart}
                onChange={(e) => this.setState({ pStart: e.target.value, pError: '' })}
                disabled={!writePermission} data-cy="period-start" />
            </td>
            <td>
              <input type="time" className="form-control whs-inline-input" value={pEnd}
                onChange={(e) => this.setState({ pEnd: e.target.value, pError: '' })}
                disabled={!writePermission} data-cy="period-end" />
            </td>
          </>
        )}
        <td>
          <Form.Select className="form-control whs-inline-input" value={pType}
            onChange={(e) => this.setState({ pType: e.target.value })}
            disabled={!writePermission} data-cy="period-type">
            {TIME_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
          </Form.Select>
        </td>
        <td>
          <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={pMultiplier}
            onChange={(e) => this.setState({ pMultiplier: e.target.value, pError: '' })}
            disabled={!writePermission} data-cy="period-multiplier" />
        </td>
        {writePermission && (
          <td className="whs-action-cell">
            <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdatePeriod} data-cy="add-period-btn">
              {isNew ? 'Add' : 'Update'}
            </Button>
            <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelPeriodEdit}>Cancel</button>
          </td>
        )}
      </tr>
    )

    const excPeriodInputRow = (isNew) => (
      <tr className="whs-edit-row">
        {writePermission && <td className="whs-drag-cell" />}
        {isHourly ? (
          <td>
            <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={epHours}
              onChange={(e) => this.setState({ epHours: e.target.value, epError: '' })}
              disabled={!writePermission} data-cy="exc-period-hours" />
          </td>
        ) : (
          <>
            <td>
              <input type="time" className="form-control whs-inline-input" value={epStart}
                onChange={(e) => this.setState({ epStart: e.target.value, epError: '' })}
                disabled={!writePermission} data-cy="exc-period-start" />
            </td>
            <td>
              <input type="time" className="form-control whs-inline-input" value={epEnd}
                onChange={(e) => this.setState({ epEnd: e.target.value, epError: '' })}
                disabled={!writePermission} data-cy="exc-period-end" />
            </td>
          </>
        )}
        <td>
          <Form.Select className="form-control whs-inline-input" value={epType}
            onChange={(e) => this.setState({ epType: e.target.value })}
            disabled={!writePermission} data-cy="exc-period-type">
            {TIME_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
          </Form.Select>
        </td>
        <td>
          <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={epMultiplier}
            onChange={(e) => this.setState({ epMultiplier: e.target.value, epError: '' })}
            disabled={!writePermission} data-cy="exc-period-multiplier" />
        </td>
        {writePermission && (
          <td className="whs-action-cell">
            <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdateExcPeriod} data-cy="add-exc-period-btn">
              {isNew ? 'Add' : 'Update'}
            </Button>
            <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelExcPeriodEdit}>Cancel</button>
          </td>
        )}
      </tr>
    )

    return (
      <div className="whs-page">
        {/* ── Left panel ─────────────────────────────────────────────────── */}
        <div className="whs-list-panel">
          <div className="whs-list-header">Available Settings</div>
          <div className="whs-list-body">
            {settings.length === 0 && <p className="whs-empty-hint">No settings yet</p>}
            {settings.map((s) => (
              <div
                key={s.id}
                className={`whs-list-item${s.id === selectedId ? ' selected' : ''}${!s.is_active ? ' inactive' : ''}`}
                onClick={() => this.selectSetting(s.id)}
                data-cy={`setting-item-${s.id}`}
              >
                <span className="whs-list-name">{s.name}</span>
                {!s.is_active && <span className="whs-inactive-badge">Inactive</span>}
              </div>
            ))}
          </div>
          {writePermission && (
            <div className="whs-list-footer">
              <button className="btn btn-sm btn-outline-primary w-100" onClick={this.startNew} data-cy="new-setting-btn">
                + New Setting
              </button>
            </div>
          )}
        </div>

        {/* ── Right panel ────────────────────────────────────────────────── */}
        <div className="whs-form-panel">

          {/* Name + Active */}
          <div className="whs-info-card mb-4">
            <div className="row g-3 align-items-center">
              <div className="col">
                <label className="input-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control form-control-input${nameError ? ' is-invalid' : ''}`}
                  value={name}
                  onChange={(e) => this.setState({ name: e.target.value, nameError: '' })}
                  disabled={!writePermission}
                  data-cy="setting-name-input"
                />
                {nameError && <small className="text-danger">{nameError}</small>}
              </div>
              <div className="col-md-3">
                <label className="input-label">Time Period Type</label>
                <Form.Select
                  className="form-control form-control-input"
                  value={timePeriodType}
                  onChange={(e) => this.changeTimePeriodType(e.target.value)}
                  disabled={!writePermission}
                  data-cy="time-period-type-select"
                >
                  {TIME_PERIOD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
                </Form.Select>
              </div>
              <div className="col-auto">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input switch-checkbox"
                    type="checkbox"
                    id="active-switch"
                    checked={isActive}
                    onChange={(e) => this.setState({ isActive: e.target.checked })}
                    disabled={!writePermission}
                    data-cy="active-checkbox"
                  />
                  <label className="form-check-label" htmlFor="active-switch">Active</label>
                </div>
              </div>
            </div>
          </div>

          {/* Info overlay shown when switching Time Period Type on a setting that already has data */}
          <Modal show={showTypeChangeWarning} onHide={() => this.setState({ showTypeChangeWarning: false })} data-cy="type-change-warning-modal">
            <Modal.Header closeButton>
              <Modal.Title>Time Period Type Changed</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Changing the Time Period Type doesn't delete any of your existing Time Periods or Date Exceptions,
              but each one will need to be re-configured — {isHourly ? 'set an Hours value' : 'set a Start and End time'} for
              every Time Period before saving.
            </Modal.Body>
            <Modal.Footer>
              <Button className="secondarybtn" onClick={() => this.setState({ showTypeChangeWarning: false })} data-cy="type-change-warning-dismiss">
                Got it
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Time Periods */}
          <div className="whs-section mb-4">
            <div className="whs-section-header">
              <span className="whs-section-label">Time Periods</span>
              <div className="whs-day-tabs" data-cy="day-tabs">
                {DAYS.map((d) => {
                  const count = (weekdayRules[d] || []).length
                  return (
                    <button
                      key={d}
                      className={`whs-day-tab${selectedDay === d ? ' active' : ''}${count > 0 ? ' has-periods' : ''}`}
                      onClick={() => this.setState({
                        selectedDay: d, selectedPeriodIdx: null, showAddPeriod: false,
                        pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
                      })}
                      data-cy={`day-tab-${d}`}
                      title={count > 0 ? `${count} period${count > 1 ? 's' : ''} configured` : `No periods for ${DAY_LABELS[d]}`}
                    >
                      {DAY_SHORT[d]}
                      {count > 0 && <span className="whs-day-count">{count}</span>}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="whs-table-wrap" data-cy="time-periods-list">
              <table className="whs-table">
                <thead>
                  <tr>
                    {writePermission && <th style={{ width: '32px' }} />}
                    {isHourly
                      ? <th style={{ width: '15%' }}>Hours</th>
                      : (
                        <>
                          <th style={{ width: '15%' }}>Start</th>
                          <th style={{ width: '15%' }}>End</th>
                        </>
                      )}
                    <th>Type</th>
                    <th style={{ width: '13%' }}>Multiplier</th>
                    {writePermission && <th style={{ width: '110px' }} />}
                  </tr>
                </thead>
                {currentPeriods.length === 0 && !showAddPeriod && (
                  <tbody>
                    <tr>
                      <td colSpan={periodColSpan} className="whs-empty-cell">
                        No time periods configured for {DAY_LABELS[selectedDay]}
                      </td>
                    </tr>
                  </tbody>
                )}
                <DragDropContext onDragEnd={this.handlePeriodDragEnd}>
                  <Droppable droppableId={`weekday-periods-${selectedDay}`}>
                    {(provided) => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {currentPeriods.map((p, i) => (
                          <Draggable key={i} draggableId={`period-${i}`} index={i} isDragDisabled={periodDragDisabled}>
                            {(dragProvided) => (
                              selectedPeriodIdx === i
                                ? React.cloneElement(periodInputRow(false), {
                                    ref: dragProvided.innerRef, ...dragProvided.draggableProps,
                                  })
                                : (
                                  <tr
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    className="whs-data-row"
                                    data-cy={`period-row-${i}`}
                                  >
                                    {writePermission && (
                                      <td className="whs-drag-cell" {...dragProvided.dragHandleProps}>
                                        <DragIndicatorIcon fontSize="small" className="drag-handle-icon" />
                                      </td>
                                    )}
                                    {isHourly
                                      ? <td>{p.hours}</td>
                                      : (
                                        <>
                                          <td>{p.start}</td>
                                          <td>{p.end}</td>
                                        </>
                                      )}
                                    <td>{formatTypeLabel(p.time_type)}</td>
                                    <td>{p.multiplier}×</td>
                                    {writePermission && (
                                      <td className="whs-action-cell">
                                        <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectPeriod(i)} />
                                        <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deletePeriod(i)} data-cy="delete-period-btn" />
                                      </td>
                                    )}
                                  </tr>
                                )
                            )}
                          </Draggable>
                        ))}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
                {showAddPeriod && <tbody>{periodInputRow(true)}</tbody>}
              </table>
              {pError && <small className="text-danger d-block mt-2">{pError}</small>}
              {writePermission && !showAddPeriod && selectedPeriodIdx === null && (
                <button
                  className="btn btn-sm btn-outline-primary mt-3"
                  onClick={() => this.setState({
                    showAddPeriod: true, selectedPeriodIdx: null,
                    pStart: '', pEnd: '', pHours: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
                  })}
                >
                  + Add Period
                </button>
              )}
            </div>
          </div>

          {/* Exception Days */}
          <div className="whs-section mb-4">
            <div className="whs-section-header">
              <span className="whs-section-label">Exception Days</span>
            </div>

            <div className="whs-table-wrap" data-cy="exceptions-list">
              <table className="whs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Time Periods</th>
                    {writePermission && <th />}
                  </tr>
                </thead>
                <tbody>
                  {exceptions.length === 0 && (
                    <tr>
                      <td colSpan={excColSpan} className="whs-empty-cell">No exception days added</td>
                    </tr>
                  )}
                  {exceptions.map((e, i) => (
                    <tr
                      key={i}
                      className={`whs-data-row${selectedExcIdx === i ? ' whs-row-selected' : ''}`}
                      data-cy={`exception-row-${i}`}
                    >
                      <td>{e.date}</td>
                      <td>{e.name}</td>
                      <td>{EXCEPTION_TYPES.find((t) => t.value === e.exception_type)?.key || e.exception_type}</td>
                      <td>
                        <span className="whs-periods-badge">
                          {(e.time_periods || []).length} period{(e.time_periods || []).length !== 1 ? 's' : ''}
                        </span>
                      </td>
                      {writePermission && (
                        <td className="whs-action-cell">
                          <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectException(i)} />
                          <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deleteException(i)} data-cy="delete-exception-btn" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Exception add/edit panel */}
            {excEditOpen && (
              <div className="whs-exc-edit-panel">
                <div className="whs-exc-edit-title">
                  {selectedExcIdx !== null ? 'Edit Exception' : 'New Exception'}
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label className="whs-field-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control form-control-input" value={eDate}
                      onChange={(e) => this.setState({ eDate: e.target.value, eError: '' })}
                      disabled={!writePermission} data-cy="exception-date" />
                  </div>
                  <div className="col-md-5">
                    <label className="whs-field-label">Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control form-control-input" value={eName}
                      onChange={(e) => this.setState({ eName: e.target.value, eError: '' })}
                      disabled={!writePermission} data-cy="exception-name" placeholder="e.g. Christmas Day" />
                  </div>
                  <div className="col-md-4">
                    <label className="whs-field-label">Exception Type</label>
                    <Form.Select className="form-control form-control-input" value={eExceptionType}
                      onChange={(e) => this.setState({ eExceptionType: e.target.value })}
                      disabled={!writePermission} data-cy="exception-type">
                      {EXCEPTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
                    </Form.Select>
                  </div>
                </div>

                {/* Time periods for this exception */}
                <div className="whs-exc-periods-section">
                  <div className="whs-exc-periods-label">Time Periods</div>
                  <div className="whs-table-wrap">
                    <table className="whs-table">
                      <thead>
                        <tr>
                          {writePermission && <th style={{ width: '32px' }} />}
                          {isHourly
                            ? <th style={{ width: '15%' }}>Hours</th>
                            : (
                              <>
                                <th style={{ width: '15%' }}>Start</th>
                                <th style={{ width: '15%' }}>End</th>
                              </>
                            )}
                          <th>Type</th>
                          <th style={{ width: '13%' }}>Multiplier</th>
                          {writePermission && <th style={{ width: '110px' }} />}
                        </tr>
                      </thead>
                      {eTimePeriods.length === 0 && !showAddExcPeriod && (
                        <tbody>
                          <tr>
                            <td colSpan={excPeriodColSpan} className="whs-empty-cell">
                              No time periods — add one below
                            </td>
                          </tr>
                        </tbody>
                      )}
                      <DragDropContext onDragEnd={this.handleExcPeriodDragEnd}>
                        <Droppable droppableId={`exception-periods-${selectedExcIdx ?? 'new'}`}>
                          {(provided) => (
                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                              {eTimePeriods.map((p, i) => (
                                <Draggable key={i} draggableId={`exc-period-${i}`} index={i} isDragDisabled={excPeriodDragDisabled}>
                                  {(dragProvided) => (
                                    selectedExcPeriodIdx === i
                                      ? React.cloneElement(excPeriodInputRow(false), {
                                          ref: dragProvided.innerRef, ...dragProvided.draggableProps,
                                        })
                                      : (
                                        <tr
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          className="whs-data-row"
                                          data-cy={`exc-period-row-${i}`}
                                        >
                                          {writePermission && (
                                            <td className="whs-drag-cell" {...dragProvided.dragHandleProps}>
                                              <DragIndicatorIcon fontSize="small" className="drag-handle-icon" />
                                            </td>
                                          )}
                                          {isHourly
                                            ? <td>{p.hours}</td>
                                            : (
                                              <>
                                                <td>{p.start}</td>
                                                <td>{p.end}</td>
                                              </>
                                            )}
                                          <td>{formatTypeLabel(p.time_type)}</td>
                                          <td>{p.multiplier}×</td>
                                          {writePermission && (
                                            <td className="whs-action-cell">
                                              <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectExcPeriod(i)} />
                                              <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deleteExcPeriod(i)} />
                                            </td>
                                          )}
                                        </tr>
                                      )
                                  )}
                                </Draggable>
                              ))}
                            </tbody>
                          )}
                        </Droppable>
                      </DragDropContext>
                      {showAddExcPeriod && <tbody>{excPeriodInputRow(true)}</tbody>}
                    </table>
                  </div>
                  {epError && <small className="text-danger d-block mt-2">{epError}</small>}
                  {writePermission && !showAddExcPeriod && selectedExcPeriodIdx === null && (
                    <button
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => this.setState({
                        showAddExcPeriod: true, selectedExcPeriodIdx: null,
                        epStart: '', epEnd: '', epHours: '', epType: 'STANDARD', epMultiplier: 1.0, epError: '',
                      })}
                    >
                      + Add Time Period
                    </button>
                  )}
                </div>

                {eError && <small className="text-danger d-block mt-3">{eError}</small>}
                <div className="whs-exc-edit-actions">
                  <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdateException} data-cy="add-exception-btn">
                    {selectedExcIdx !== null ? 'Update Exception' : 'Add Exception'}
                  </Button>
                  <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelExceptionEdit}>Cancel</button>
                </div>
              </div>
            )}

            {writePermission && !excEditOpen && (
              <button
                className="btn btn-sm btn-outline-primary mt-3"
                onClick={this.startAddException}
              >
                + Add Exception
              </button>
            )}
          </div>

          {/* Footer */}
          {writePermission && (
            <div className="whs-form-footer">
              <Button className="secondarybtn cancelbtn" onClick={this.cancel} data-cy="cancel-btn">Cancel</Button>
              <Button className="secondarybtn" onClick={this.save} disabled={saving} data-cy="save-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getWorkHourSettings, getWorkHourSetting, createWorkHourSetting, updateWorkHourSetting }, dispatch)

export default connect(null, mapDispatchToProps)(WorkHourSettingsPage)
